import React, {
  useState,
  useCallback,
  useRef,
  useEffect,
  useMemo,
} from "react";
import styled from "@emotion/styled";
import { useSelector, useDispatch } from "react-redux";
import { useHistory } from "react-router";
import { sortBy } from "lodash-es";
import { scrollbarDe } from "../../styles/styledSnippets";
import {
  IconSparkle,
  IconDashboard,
  IconSection,
  IconChat,
  IconColumn,
} from "./icons";
import SearchResultRow from "./SearchResultRow";
import QuickAccessRow from "./QuickAccessRow";
import AskAIButton from "./AskAIButton";
import { getAiSuggestions } from "../../aiSuggestions";
import { getPages } from "../../store/actions";
import { highlightWithMark } from "../../utils/jsx";
import PageDisplayName from "../PageDisplayName";

// --- Styled components ---

const SearchWrapper = styled.div`
  position: relative;
  display: flex;
  gap: 8px;
  width: 100%;
  align-items: center;
`;

const SearchInputWrapper = styled.div`
  position: relative;
  flex: 1;
  min-width: 0;
`;

const InputContainer = styled.div(
  ({ theme }) => `
  display: flex;
  align-items: center;
  gap: 10px;
  border-radius: 6px;
  border: 1px solid ${theme.divider};
  background: ${
    theme.type === "dark"
      ? theme.background.primary
      : theme.background.secondary
  };
  padding: 0 14px;
  height: 38px;
  transition: border-color 0.15s, background 0.15s;

  &:focus-within {
    border-color: ${theme.primary};
    background: ${theme.type === "dark" ? "#2a2a2a" : "#fff"};
  }
`
);

const SearchIcon = styled.span`
  color: ${({ theme }) => theme.text.disabled};
  flex-shrink: 0;
  display: flex;
`;

const SearchInput = styled.input`
  flex: 1;
  min-width: 0;
  background: transparent;
  color: ${({ theme }) => theme.text.primary};
  font-size: 14px;
  border: none;
  outline: none;

  &::placeholder {
    color: ${({ theme }) => theme.text.disabled};
  }
`;

const ResultsPanel = styled.div(
  ({ theme }) => `
  position: absolute;
  top: 100%;
  left: 0;
  z-index: 50;
  margin-top: 6px;
  min-width: 280px;
  border-radius: 6px;
  border: 1px solid ${theme.divider};
  background: ${
    theme.type === "dark"
      ? theme.background.secondary
      : theme.background.primary
  };
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.25);
  overflow: hidden;
`
);

const SearchResultsSection = styled.div(
  ({ theme }) => `
  overflow-y: auto;
  ${scrollbarDe(theme)}
`
);

const SectionHeader = styled.p(
  ({ theme }) => `
  position: sticky;
  top: 0;
  z-index: 1;
  margin: 0;
  padding: 8px 12px;
  font-size: 11px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: ${theme.text.disabled};
  background: ${
    theme.type === "dark"
      ? theme.background.secondary
      : theme.background.primary
  };
`
);

const SuggestionsSection = styled.div(
  ({ theme }) => `
  padding: 36px;
  padding-bottom: 12px;

  & > div + div {
    margin-top: 16px;
  }
`
);

const SuggestionLabel = styled.p(
  ({ theme }) => `
  margin: 0 0 8px;
  font-size: 11px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: ${theme.text.disabled};
`
);

const AISuggestionChip = styled.button(
  ({ theme }) => `
  display: inline-flex;
  align-items: center;
  gap: 8px;
  border-radius: 9999px;
  border: 1px solid ${theme.primary}4D;
  background: ${theme.primary}1A;
  padding: 8px 14px;
  font-size: 13px;
  font-weight: 500;
  color: ${theme.primary};
  cursor: pointer;
  transition: all 0.15s;

  &:hover {
    border-color: ${theme.primary}80;
    background: ${theme.primary}33;
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px ${theme.primary}66;
  }
`
);

const ChipGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
`;

const SuggestionChip = styled.button(
  ({ theme }) => `
  display: inline-flex;
  align-items: center;
  gap: 8px;
  border-radius: 9999px;
  border: 1px solid ${
    theme.type === "dark" ? "rgba(255,255,255,0.12)" : theme.divider
  };
  background: ${theme.hover};
  padding: 8px 14px;
  font-size: 13px;
  color: ${
    theme.type === "dark" ? "rgba(255,255,255,0.8)" : theme.text.primary
  };
  cursor: pointer;
  transition: all 0.15s;

  &:hover {
    border-color: ${
      theme.type === "dark" ? "rgba(255,255,255,0.2)" : theme.borderColor
    };
    background: ${
      theme.type === "dark"
        ? "rgba(255,255,255,0.08)"
        : theme.background.highlightedOnPrimary
    };
    color: ${theme.text.primary};
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px ${theme.primary}66;
  }
`
);

const GroupDivider = styled.hr(
  ({ theme }) => `
  border: none;
  border-top: 1px solid ${
    theme.type === "dark" ? "rgba(255,255,255,0.08)" : theme.divider
  };
  margin: 14px 0;
`
);

const PanelBody = styled.div`
  display: flex;
`;

const PanelLeft = styled.div(
  ({ theme }) => `
  flex: 1;
  min-width: 0;
  overflow-y: auto;
  max-height: 620px;
  ${scrollbarDe(theme)}
`
);

const PanelRight = styled.div(
  ({ theme }) => `
  width: 380px;
  flex-shrink: 0;
  border-left: 1px solid ${
    theme.type === "dark" ? "rgba(255,255,255,0.08)" : theme.divider
  };
  overflow-y: auto;
  max-height: 620px;
  ${scrollbarDe(theme)}
`
);

const QuickAccessSection = styled.div(
  ({ theme }) => `
  padding: 0 0 8px;
  border-top: 1px solid ${
    theme.type === "dark" ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.06)"
  };

  & > div:first-of-type {
    padding: 16px 36px 6px;
  }
`
);

const QuickAccessHeading = styled.div(
  ({ theme }) => `
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: ${theme.text.secondary};
`
);

const QuickAccessHelper = styled.div(
  ({ theme }) => `
  font-size: 11px;
  color: ${theme.text.disabled};
  margin-top: 2px;
  font-weight: 400;
  letter-spacing: 0;
  text-transform: none;
`
);

const ButtonWrapper = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;
`;

// --- Constants ---

const STATIC_SUGGESTIONS = getAiSuggestions();

const DATA_EXPLORER_PATH = "/admin/data-settings/data-explorer";

const sparkleSmallStyle = { height: 14, width: 14, flexShrink: 0 };

function formatCompactTime(dateStr) {
  if (!dateStr) return "";
  const now = Date.now();
  const then = new Date(dateStr).getTime();
  const diffMs = now - then;
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return "Just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days === 1) return "Yesterday";
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  return `${months}mo ago`;
}

// --- Component ---

export default function AISearch({ onAskAi, onResumeConversation }) {
  const history = useHistory();
  const dispatch = useDispatch();
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);
  const panelRef = useRef(null);
  const [panelMaxWidth, setPanelMaxWidth] = useState(null);
  const [recentConversations, setRecentConversations] = useState([]);
  const conversationsLoadedRef = useRef(false);

  const tabs = useSelector((state) => state.layout.tabs);

  const [columnIndex, setColumnIndex] = useState([]);
  const pagesDataLoadedRef = useRef(false);

  const aiResultsModalOpen = useSelector(
    (state) => state.dashboard.aiSidebar.aiResultsModalOpen
  );
  const prevModalOpenRef = useRef(aiResultsModalOpen);

  // Invalidate conversation cache when the modal closes so the next
  // focus refetches and includes any newly created conversation.
  useEffect(() => {
    if (prevModalOpenRef.current && !aiResultsModalOpen) {
      conversationsLoadedRef.current = false;
    }
    prevModalOpenRef.current = aiResultsModalOpen;
  }, [aiResultsModalOpen]);

  const closePanel = useCallback(() => {
    setQuery("");
    setOpen(false);
  }, []);

  // Keep panel within viewport
  useEffect(() => {
    if (!open || !wrapperRef.current) return;
    const gap = 16;
    const preferredMax = 920;
    const update = () => {
      if (!wrapperRef.current) return;
      const rect = wrapperRef.current.getBoundingClientRect();
      const spaceRight = window.innerWidth - rect.left - gap;
      setPanelMaxWidth(Math.min(preferredMax, Math.max(280, spaceRight)));
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(wrapperRef.current);
    window.addEventListener("resize", update);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", update);
    };
  }, [open]);

  // Click outside
  useEffect(() => {
    function handleClick(e) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  // Search logic - matches GlobalSearch's approach
  const searchResults = useMemo(() => {
    if (!query || query.length < 2)
      return { blocks: [], pages: [], columns: [] };

    const lowerQuery = query.toLowerCase();
    const wordBoundaryStartRegex = new RegExp(
      `^${RegExp.escape(query)}\\b`,
      "i"
    );
    const startRegex = new RegExp(`^${RegExp.escape(query)}`, "i");
    const wordBoundaryRegex = new RegExp(`\\b${RegExp.escape(query)}\\b`, "i");

    const sortPriority = (v) =>
      wordBoundaryStartRegex.test(v.search)
        ? 0
        : wordBoundaryRegex.test(v.search)
        ? 1
        : startRegex.test(v.search)
        ? 2
        : 3;

    const allBlocks = tabs
      .map((v) => v.blocks?.map((block) => ({ ...block, page: v })))
      .flat()
      .filter(Boolean);

    const blockResults = sortBy(
      allBlocks
        .filter((v) => v.title && v.title.toLowerCase().includes(lowerQuery))
        .map((v) => ({
          title: v.title,
          slug: v.page.slug,
          page: v.page,
          search: v.title,
        })),
      sortPriority
    ).slice(0, 8);

    const pageResults = sortBy(
      tabs
        .filter((v) => {
          const searchable = `${v.displayName || ""} ${
            v.parentName || ""
          }`.toLowerCase();
          return searchable.includes(lowerQuery);
        })
        .map((v) => ({
          displayName: v.displayName,
          slug: v.slug,
          page: v,
          search: v.displayName,
        })),
      sortPriority
    ).slice(0, 8);

    const columnResults = sortBy(
      columnIndex
        .filter(
          (v) =>
            v.displayName.toLowerCase().includes(lowerQuery) ||
            v.fieldName.toLowerCase().includes(lowerQuery)
        )
        .map((v) => ({ ...v, search: v.displayName })),
      sortPriority
    ).slice(0, 8);

    return { blocks: blockResults, pages: pageResults, columns: columnResults };
  }, [query, tabs, columnIndex]);

  const handleFocus = useCallback(() => {
    setOpen(true);
    if (!tabs || tabs.length === 0) {
      dispatch(getPages());
    }
    if (!conversationsLoadedRef.current && onResumeConversation) {
      conversationsLoadedRef.current = true;
      import("../../api/aiConversations").then(({ listAiConversations }) =>
        listAiConversations()
          .then((all) => {
            const dataExplorer = all
              .filter((c) => c.type === "data_explorer")
              .slice(0, 5);
            setRecentConversations(dataExplorer);
          })
          .catch(() => {})
      );
    }
    if (!pagesDataLoadedRef.current) {
      pagesDataLoadedRef.current = true;
      const buildIndex = () =>
        import("../../axios").then(({ default: axios }) =>
          axios
            .get("/api/v1/pages")
            .then((res) => {
              const pages = res.data.data;
              const entries = [];
              const seen = new Set();
              for (const page of pages) {
                for (const block of page.blocks || []) {
                  for (const vis of block.visualizations || []) {
                    // Build display name lookup from overrides
                    const displayNames = {};
                    for (const o of vis.overrides || []) {
                      const key = o.oAlias || o.name;
                      if (key && o.mapping?.displayName) {
                        displayNames[key] = o.mapping.displayName;
                      }
                    }

                    // Index from subTitles (authoritative column source)
                    const settings = vis.settings;
                    if (
                      settings &&
                      !Array.isArray(settings) &&
                      Array.isArray(settings.subTitles)
                    ) {
                      for (const colKey of settings.subTitles.flat(Infinity)) {
                        if (!colKey) continue;
                        const dedup = `${colKey}|${block.uuid}|${page.slug}`;
                        if (seen.has(dedup)) continue;
                        seen.add(dedup);

                        let displayName = displayNames[colKey];
                        if (!displayName && colKey.startsWith("fn::")) {
                          displayName = colKey.split("::").pop();
                        }
                        if (!displayName) displayName = colKey;

                        entries.push({
                          fieldName: colKey,
                          displayName,
                          blockTitle: block.title,
                          blockUuid: block.uuid,
                          pageSlug: page.slug,
                          pageDisplayName: page.displayName,
                          search: displayName,
                        });
                      }
                    }

                    // Fallback: index overrides for visualizations without subTitles
                    if (
                      !settings ||
                      Array.isArray(settings) ||
                      !Array.isArray(settings.subTitles)
                    ) {
                      for (const override of vis.overrides || []) {
                        if (!override.name) continue;
                        const columnKey = override.oAlias || override.name;
                        const dedup = `${columnKey}|${block.uuid}|${page.slug}`;
                        if (seen.has(dedup)) continue;
                        seen.add(dedup);
                        const displayName =
                          override.mapping?.displayName || override.name;
                        entries.push({
                          fieldName: columnKey,
                          displayName,
                          blockTitle: block.title,
                          blockUuid: block.uuid,
                          pageSlug: page.slug,
                          pageDisplayName: page.displayName,
                          search: displayName,
                        });
                      }
                    }
                  }
                }
              }
              setColumnIndex(entries);
            })
            .catch(() => {
              pagesDataLoadedRef.current = false;
            })
        );
      if (window.requestIdleCallback) {
        window.requestIdleCallback(buildIndex);
      } else {
        setTimeout(buildIndex, 200);
      }
    }
  }, [tabs, dispatch, onResumeConversation]);

  const handleResultOpen = useCallback(
    (slug) => {
      history.push(`/dashboard/${slug}`);
      closePanel();
    },
    [history, closePanel]
  );

  const handleColumnOpen = useCallback(
    (column) => {
      history.push(
        `/dashboard/${column.pageSlug}?highlightColumn=${encodeURIComponent(
          column.fieldName
        )}&highlightBlock=${encodeURIComponent(column.blockUuid)}`
      );
      closePanel();
    },
    [history, closePanel]
  );

  const navigateToExplorer = useCallback(
    (questionText) => {
      if (onAskAi) {
        onAskAi(questionText?.trim());
        closePanel();
        return;
      }
      const path = questionText?.trim()
        ? `${DATA_EXPLORER_PATH}?q=${encodeURIComponent(questionText.trim())}`
        : DATA_EXPLORER_PATH;
      history.push(path);
      closePanel();
    },
    [onAskAi, closePanel, history]
  );

  return (
    <SearchWrapper ref={wrapperRef}>
      <SearchInputWrapper>
        <InputContainer>
          <SearchIcon aria-hidden>
            <svg
              width="16"
              height="16"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
          </SearchIcon>
          <SearchInput
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onFocus={handleFocus}
            onKeyDown={(e) => {
              if (e.key === "Enter" && query.trim()) {
                e.preventDefault();
                navigateToExplorer(query);
              }
            }}
            placeholder="Search pages or ask AI..."
            aria-label="Ask AI about your data"
            autoComplete="off"
          />
        </InputContainer>

        {open && (
          <ResultsPanel
            ref={panelRef}
            style={
              panelMaxWidth != null
                ? { width: panelMaxWidth, maxWidth: panelMaxWidth - 1 }
                : { maxWidth: "min(920px, calc(100vw - 2rem))" }
            }
          >
            <PanelBody>
              <PanelLeft>
                <SuggestionsSection>
                  <div>
                    <SuggestionLabel>
                      Show me results in the AI Explorer
                    </SuggestionLabel>
                    <AISuggestionChip
                      type="button"
                      onClick={() => navigateToExplorer(query)}
                      aria-label="Open AI Explorer to ask a question"
                    >
                      <IconSparkle style={sparkleSmallStyle} />
                      <span>Ask AI in Explorer →</span>
                    </AISuggestionChip>
                  </div>
                  {STATIC_SUGGESTIONS.length > 0 && (
                    <div>
                      <GroupDivider />
                      <SuggestionLabel style={{ marginBottom: 12 }}>
                        Top suggested data requests
                      </SuggestionLabel>
                      {STATIC_SUGGESTIONS.map((section, sIdx) => (
                        <div
                          key={section.group || sIdx}
                          style={sIdx > 0 ? { marginTop: 14 } : undefined}
                        >
                          {section.group && (
                            <SuggestionLabel
                              style={{ fontSize: 10, margin: "0 0 8px" }}
                            >
                              {section.group}
                            </SuggestionLabel>
                          )}
                          <ChipGroup
                            role="group"
                            aria-label={section.group || "Suggested prompts"}
                          >
                            {section.items.map((item, idx) => (
                              <SuggestionChip
                                key={`${item.label}-${sIdx}-${idx}`}
                                type="button"
                                onClick={() => navigateToExplorer(item.prompt)}
                                aria-label={`Ask: ${item.label}`}
                              >
                                <span>{item.label}</span>
                              </SuggestionChip>
                            ))}
                          </ChipGroup>
                        </div>
                      ))}
                    </div>
                  )}
                </SuggestionsSection>
                {recentConversations.length > 0 && (
                  <QuickAccessSection>
                    <div>
                      <QuickAccessHeading>Quick Access</QuickAccessHeading>
                      <QuickAccessHelper>
                        Jump back into recent analysis
                      </QuickAccessHelper>
                    </div>
                    {recentConversations.map((conv) => (
                      <QuickAccessRow
                        key={conv.uuid}
                        icon={<IconChat style={{ width: 14, height: 14 }} />}
                        title={conv.title || "Untitled conversation"}
                        meta={formatCompactTime(conv.createdAt)}
                        onClick={() => {
                          onResumeConversation(conv);
                          closePanel();
                        }}
                      />
                    ))}
                  </QuickAccessSection>
                )}
              </PanelLeft>
              {(searchResults.blocks.length > 0 ||
                searchResults.pages.length > 0 ||
                searchResults.columns.length > 0) && (
                <PanelRight>
                  <SearchResultsSection>
                    {searchResults.blocks.length > 0 && (
                      <>
                        <SectionHeader>Block results</SectionHeader>
                        {searchResults.blocks.map((block) => (
                          <SearchResultRow
                            key={`block-${block.slug}-${block.title}`}
                            icon={
                              <IconSection style={{ width: 16, height: 16 }} />
                            }
                            title={highlightWithMark(block.title, query)}
                            subtitle={block.page.displayName}
                            onOpen={() => handleResultOpen(block.slug)}
                            onExplore={() => navigateToExplorer(block.title)}
                          />
                        ))}
                      </>
                    )}
                    {searchResults.pages.length > 0 && (
                      <>
                        <SectionHeader>Page results</SectionHeader>
                        {searchResults.pages.map((page) => (
                          <SearchResultRow
                            key={`page-${page.slug}`}
                            icon={
                              <IconDashboard
                                style={{ width: 16, height: 16 }}
                              />
                            }
                            title={
                              <PageDisplayName
                                page={page.page}
                                displayName={highlightWithMark(
                                  page.displayName,
                                  query
                                )}
                              />
                            }
                            onOpen={() => handleResultOpen(page.slug)}
                            onExplore={() =>
                              navigateToExplorer(page.displayName)
                            }
                          />
                        ))}
                      </>
                    )}
                    {searchResults.columns.length > 0 && (
                      <>
                        <SectionHeader>Column matches</SectionHeader>
                        {searchResults.columns.map((col) => (
                          <SearchResultRow
                            key={`col-${col.blockUuid}-${col.fieldName}`}
                            icon={
                              <IconColumn style={{ width: 16, height: 16 }} />
                            }
                            title={highlightWithMark(col.displayName, query)}
                            subtitle={`${col.blockTitle} → ${col.pageDisplayName}`}
                            onOpen={() => handleColumnOpen(col)}
                          />
                        ))}
                      </>
                    )}
                  </SearchResultsSection>
                </PanelRight>
              )}
            </PanelBody>
          </ResultsPanel>
        )}
      </SearchInputWrapper>
      <ButtonWrapper>
        <AskAIButton query={query} onClose={closePanel} onAskAi={onAskAi} />
      </ButtonWrapper>
    </SearchWrapper>
  );
}
